import { createAsyncThunk, createAction } from '@reduxjs/toolkit'
import { AxiosResponse } from 'axios'
import type { RootState } from 'app'
import Api, { paths } from 'shared/services/Api'
import {
  ContractorsDataResponse,
  AddContractorResponse,
  FetchContractorByTaxIdResponse,
  ContractorFilters,
  ContractorDetails,
  AddContractorRequest,
  Option,
  GetContractorsAutocompleteRequest,
  GetContractorsAutocompleteResponse,
  ContractorContact,
  ContractorContactPutRequest,
  ContractorContactPostRequest,
  ContractorContactDeleteRequest,
  ServerError,
  LoadingStatus,
  RejectValue,
} from 'shared/types'
import { getContractorDetails } from './selectors'
import {
  EditContractorPayload,
  SetContractorAddressPayload,
  SetContractorNamePayload,
} from './actions.types'

const NAMESPACE = 'contractors'

export const clearContractors = createAction(`${NAMESPACE}/clearContractors`)
export const toggleContractor = createAction<{
  contractor: Option<string, number>
}>(`${NAMESPACE}/toggleContractor`)
export const clearSelectedContractors = createAction(
  `${NAMESPACE}/clearSelectedContractors`
)
export const closeAddContractorSuccessDialog = createAction(
  `${NAMESPACE}/closeAddContractorSuccessDialog`
)
export const clearAddContractorError = createAction(
  `${NAMESPACE}/clearAddContractorError`
)
export const setAddContactDialogOpen = createAction<boolean>(
  `${NAMESPACE}/setAddContactDialogOpen`
)
export const setAddContractorDialogOpen = createAction<boolean>(
  `${NAMESPACE}/setAddContractorDialogOpen`
)
export const setAddContractorDialogLoadingStatus = createAction<LoadingStatus>(
  `${NAMESPACE}/setAddContractorDialogLoadingStatus`
)

export const clearTaxNumberError = createAction(
  `${NAMESPACE}/clearTaxNumberError`
)
export const resetContractorDetailsState = createAction(
  `${NAMESPACE}/resetContractorDetailsState`
)
export const resetContractorContactsState = createAction(
  `${NAMESPACE}/resetContractorContactsState`
)
export const resetDialogsState = createAction(`${NAMESPACE}/resetDialogsState`)

export const fetchContractorByTaxNumber = createAsyncThunk<
  { response: FetchContractorByTaxIdResponse },
  { taxNumber: string; countryId: number },
  { rejectValue: { errorCode?: string } | RejectValue[] }
>(
  `${NAMESPACE}/fetchContractorByTaxNumber`,
  async ({ countryId, taxNumber }, thunkAPI) => {
    try {
      const response = await Api.get(`${paths.Contractors.search}`, {
        params: {
          taxNumber,
          countryId,
        },
      })
      return { response: response.data }
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  }
)

export const getContractorsAutocomplete = createAsyncThunk<
  { response: GetContractorsAutocompleteResponse },
  { request: GetContractorsAutocompleteRequest },
  { rejectValue: string }
>(`${NAMESPACE}/getContractorsAutocomplete`, async ({ request }, thunkAPI) => {
  try {
    const response = await Api.get(paths.Contractors.autocomplete, {
      params: request,
    })
    return { response: response.data }
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const getContractors = createAsyncThunk<
  { response: ContractorsDataResponse },
  ContractorFilters | undefined,
  { rejectValue: string }
>(`${NAMESPACE}/getContractors`, async (filters, thunkAPI) => {
  try {
    const response = await Api.get(paths.Contractors.root, {
      params: filters,
    })
    return { response: response.data }
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const addContractor = createAsyncThunk<
  {
    response: AddContractorResponse &
      AddContractorRequest & {
        openSuccessDialog: boolean
      }
  },
  { request: Partial<AddContractorRequest> & { openSuccessDialog: boolean } },
  { rejectValue: string & ServerError }
>(`${NAMESPACE}/addContractor`, async ({ request }, thunkAPI) => {
  try {
    const { openSuccessDialog, ...data } = request
    const response = await Api.post(paths.Contractors.root, data)
    if (!response?.data) throw response
    return {
      response: {
        ...response.data,
        openSuccessDialog,
      },
    }
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const editContractor = createAsyncThunk<
  EditContractorPayload,
  Partial<EditContractorPayload>,
  { rejectValue: string }
>(
  `${NAMESPACE}/editContractor`,
  async (payload, { getState, rejectWithValue }) => {
    try {
      const { data } = getContractorDetails(getState() as RootState)
      const {
        buildingNumber,
        street,
        apartmentNumber,
        city,
        zipCode,
        id,
        name,
      } = data as ContractorDetails
      const body = {
        buildingNumber,
        street,
        apartmentNumber,
        city,
        zipCode,
        id,
        name,
        ...payload,
      }
      await Api.put(`${paths.Contractors.root}/${id}`, body)
      return body
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const getContractor = createAsyncThunk<
  { response: ContractorDetails },
  number,
  { rejectValue: string }
>(`${NAMESPACE}/getContractor`, async (id, thunkAPI) => {
  try {
    const response = await Api.get(`${paths.Contractors.root}/${id}`)
    return { response: response.data }
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const getContractorContacts = createAsyncThunk<
  { response: ContractorContact[] },
  number,
  { rejectValue: string }
>(`${NAMESPACE}/getContractorContacts`, async (id, thunkAPI) => {
  try {
    const response = await Api.get(`${paths.Contractors.root}/${id}/contacts`)
    return { response: response.data }
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const editContractorContact = createAsyncThunk<
  { response: ContractorContact },
  { data: ContractorContactPutRequest },
  { rejectValue: string }
>(
  `${NAMESPACE}/editContractorContact`,
  async ({ data: { contractorId, ...contact } }, thunkAPI) => {
    try {
      const response = await Api.put(
        `${paths.Contractors.root}/${contractorId}/contacts/${contact.id}`,
        {
          ...contact,
          contractorId,
        }
      )
      return { response: response.data }
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  }
)

export const addContractorContact = createAsyncThunk<
  { response: ContractorContact },
  { data: ContractorContactPostRequest },
  { rejectValue: string }
>(
  `${NAMESPACE}/addContractorContact`,
  async ({ data: { contractorId, ...contact } }, thunkAPI) => {
    try {
      const response = await Api.post(
        `${paths.Contractors.root}/${contractorId}/contacts`,
        {
          ...contact,
          contractorId,
        }
      )
      return { response: response.data }
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  }
)

export const deleteContractorContact = createAsyncThunk<
  { response: AxiosResponse & { id: number } },
  { data: ContractorContactDeleteRequest },
  { rejectValue: string }
>(
  `${NAMESPACE}/deleteContractorContact`,
  async ({ data: { contractorId, id } }, thunkAPI) => {
    try {
      const response = await Api.delete(
        `${paths.Contractors.root}/${contractorId}/contacts/${id}`,
        {
          data: {
            contractorId,
            id,
          },
        }
      )
      return { response: { ...response.data, id } }
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  }
)

export const getContractorContactStatus = createAsyncThunk<
  { response: ContractorDetails },
  number,
  { rejectValue: string }
>(`${NAMESPACE}/getContractorContactStatus`, async (id, thunkAPI) => {
  try {
    const response = await Api.get(`${paths.Contractors.root}/${id}`)
    return { response: response.data }
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const setContractorName = createAsyncThunk<
  void,
  SetContractorNamePayload,
  { rejectValue: string }
>(`${NAMESPACE}/setContractorName`, async (data, { rejectWithValue }) => {
  try {
    await Api.put(`${paths.Contractors.root}/${data.id}/set-name`, data)
  } catch (error) {
    return rejectWithValue(error)
  }
})

export const setContractorAddress = createAsyncThunk<
  void,
  SetContractorAddressPayload,
  { rejectValue: string }
>(`${NAMESPACE}/setContractorAddress`, async (data, { rejectWithValue }) => {
  try {
    await Api.put(`${paths.Contractors.root}/${data.id}/set-address`, data)
  } catch (error) {
    return rejectWithValue(error)
  }
})

const actions = {
  toggleContractor,
  clearSelectedContractors,
  getContractors,
  getContractorsAutocomplete,
  clearContractors,
  getContractor,
  getContractorContacts,
  getContractorContactStatus,
  editContractorContact,
  addContractorContact,
  deleteContractorContact,
  addContractor,
  closeAddContractorSuccessDialog,
  clearAddContractorError,
  setAddContactDialogOpen,
  setAddContractorDialogOpen,
  clearTaxNumberError,
  setAddContractorDialogLoadingStatus,
  fetchContractorByTaxNumber,
}

export default actions
